import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Plus, History, Wallet, WalletCards,
  User, LogOut, ChevronLeft, ChevronRight, BarChart2
} from 'lucide-react';

const Sidebar = ({ user, onLogout, collapsed, setCollapsed, onQuickAdd }) => {
  const location = useLocation();

  // Extract initials from email (e.g., "john@example.com" -> "JO")
  const userInitials = user?.email?.substring(0, 2).toUpperCase() || '??';

  const navItems = [
    { name: 'Home', path: '/home', icon: LayoutDashboard },
    { name: 'Transactions', path: '/transactions', icon: History },
    { name: 'Budget Allocation', path: '/budget-allocation', icon: WalletCards },
    { name: 'Money Tracker', path: '/money-tracker', icon: Wallet },
    { name: 'Insights', path: '/insights', icon: BarChart2 },
    { name: 'Profile', path: '/profile', icon: User }
  ];
  const mobileNavItems = [
    { name: 'Home', path: '/home', icon: LayoutDashboard, label: 'Home' },
    { name: 'Transactions', path: '/transactions', icon: History, label: 'Transactions' },
    { name: 'Budget', path: '/budget-allocation', icon: WalletCards, label: 'Budget' },
    { name: 'Tracker', path: '/money-tracker', icon: Wallet, label: 'Tracker' },
    { name: 'Insights', path: '/insights', icon: BarChart2, label: 'Insights' },
    { name: 'Profile', path: '/profile', icon: User, label: 'Profile' }
  ];

  return (
    <>
    <aside className={`fixed top-0 left-0 z-50 hidden h-screen bg-slate-950 text-slate-300 transition-all duration-300 border-r border-slate-800 md:block ${collapsed ? 'w-20' : 'w-64'}`}>
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-slate-800/50">
          {!collapsed && <span className="font-bold text-xl text-white tracking-tight uppercase italic">Spera</span>}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1.5 rounded-lg bg-slate-900 hover:bg-indigo-600 transition-colors">
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1">
          <button onClick={onQuickAdd} className={`mb-4 flex w-full items-center gap-3 rounded-2xl bg-[#0077FF] px-3 py-3 text-white transition hover:bg-[#0067DE] ${collapsed ? 'justify-center' : ''}`}>
            <Plus size={20} />
            {!collapsed && <span className="text-sm font-bold">Quick add</span>}
          </button>
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${location.pathname === item.path || (item.path === '/home' && location.pathname === '/dashboard') || (item.path === '/activity' && location.pathname === '/transactions') || (item.path === '/insights' && location.pathname === '/statistics') ? 'bg-[#EAF4FF] text-[#003B80]' : 'hover:bg-slate-900 hover:text-white'}`}>
              <item.icon size={20} strokeWidth={1.7} />
              {!collapsed && <span className="font-medium text-sm">{item.name}</span>}
            </Link>
          ))}
        </nav>

        {/* ✅ NEW: Global Identity Badge */}
        <div className="p-4 border-t border-slate-800/50">
          <div className={`flex items-center gap-3 p-2 rounded-2xl bg-slate-900/50 mb-2 ${collapsed ? 'justify-center' : ''}`}>
             <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center text-[10px] font-black text-white shrink-0 shadow-lg shadow-indigo-500/20">
                {userInitials}
             </div>
             {!collapsed && (
               <div className="overflow-hidden">
                 <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none">Account</p>
                 <p className="text-xs font-bold text-slate-200 truncate">{user?.email}</p>
               </div>
             )}
          </div>
          
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-red-500/10 hover:text-red-400 transition-all text-slate-400">
            <LogOut size={20} />
            {!collapsed && <span className="font-medium text-sm">Sign Out</span>}
          </button>
        </div>
      </div>
    </aside>
    {location.pathname !== '/form' && (
      <button onClick={onQuickAdd} className="fixed bottom-[calc(5.25rem+env(safe-area-inset-bottom))] right-4 z-[55] grid h-16 w-16 place-items-center rounded-[22px] bg-[#0077FF] text-white shadow-[0_10px_30px_rgba(0,119,255,0.28)] transition active:scale-95 md:hidden" aria-label="Quick add">
        <Plus size={28} />
      </button>
    )}
    <nav className="m3-nav fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-[#FFFBFE]/95 px-1 pb-[calc(0.35rem+env(safe-area-inset-bottom))] pt-1.5 backdrop-blur md:hidden" aria-label="Primary navigation">
      <div className="mx-auto grid w-full max-w-[640px] grid-cols-6 items-end gap-0">
        {mobileNavItems.map((item) => {
          const active = location.pathname === item.path
            || (item.path === '/home' && location.pathname === '/dashboard')
            || (item.path === '/activity' && location.pathname === '/transactions')
            || (item.path === '/insights' && location.pathname === '/statistics');
          const Icon = item.icon;
          return (
          <Link
            key={item.path}
            to={item.path}
            className={`flex h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-2xl px-0.5 transition active:scale-95 ${active ? 'text-[#0077FF]' : 'text-slate-500'}`}
          >
            <span className={`grid h-7 w-12 place-items-center rounded-full ${active ? 'bg-[#EAF4FF]' : ''}`}><Icon size={18} strokeWidth={2} /></span>
            <span className="max-w-full truncate text-[9px] font-bold leading-none">{item.label}</span>
          </Link>
          );
        })}
      </div>
    </nav>
    </>
  );
};

export default Sidebar;
